import { motion } from 'framer-motion';
import { MapPin, Shield, CreditCard, Bell, CheckCircle, Wallet, Car, User, ArrowRight } from 'lucide-react';
import Seo from '../components/Seo';
import HeaderSection from './HeaderSection';
import Footer from './Footer';
import ScrollToTop from './ScrollToTop';

const JOURNEYS = [
  {
    key: 'rider',
    label: 'For Riders',
    color: 'primary',
    cta: { Icon: User, label: 'Download Rider App', url: 'https://play.google.com/store/apps/details?id=com.elt.totowala' },
    steps: [
      { Icon: MapPin, title: 'Book a ride', desc: 'Set your pickup and drop location. Nearby drivers appear on the map instantly.' },
      { Icon: Shield, title: 'Ride safely', desc: 'Every driver is verified. Track your trip live and share it with family.' },
      { Icon: CreditCard, title: "Pay what's fair", desc: 'No platform fees, no hidden charges, no surprise surge.' },
    ],
  },
  {
    key: 'driver',
    label: 'For Drivers',
    color: 'teal',
    cta: { Icon: Car, label: 'Download Driver App', url: 'https://play.google.com/store/apps/details?id=com.elt.totowalafordrivers' },
    steps: [
      { Icon: Bell, title: 'Instant alert', desc: 'A new ride request shows route, distance, and earnings upfront.' },
      { Icon: CheckCircle, title: 'Accept or skip', desc: 'No pressure, no penalty. You decide which rides to take.' },
      { Icon: Wallet, title: 'Keep more, earn more', desc: 'Lowest commission in the business, paid out instantly.' },
    ],
  },
];

const COLOR_CLASSES = {
  primary: { text: 'text-primary', solid: 'bg-primary' },
  teal: { text: 'text-teal-dark dark:text-teal', solid: 'bg-teal-dark' },
};

const HowItWorksPage = () => {
  return (
    <>
      <Seo
        title="How It Works - TotoWala | Booking & Driving Explained"
        description="How TotoWala works for riders and drivers: book a ride in three steps, or sign up as a driver and start earning with zero commission across India."
        canonical="/how-it-works"
      />
      <div className="min-h-screen bg-white dark:bg-dark-bg">
        <HeaderSection />

        <section className="bg-surface-alt dark:bg-dark-surface-alt py-12 lg:py-14 px-4">
          <div className="max-w-[760px] mx-auto text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
              className="font-display text-3xl sm:text-4xl font-semibold leading-tight text-ink dark:text-dark-ink mb-4"
            >
              How It Works
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.06 }}
              className="text-base text-ink-soft dark:text-dark-ink-soft leading-relaxed max-w-lg mx-auto"
            >
              Three steps, two journeys — whether you are booking a ride or driving one.
            </motion.p>
          </div>
        </section>

        {JOURNEYS.map((journey, ji) => {
          const c = COLOR_CLASSES[journey.color];
          return (
            <section key={journey.key} className={`py-12 lg:py-14 px-4 ${ji % 2 === 0 ? 'bg-white dark:bg-dark-bg' : 'bg-surface-alt dark:bg-dark-surface-alt'}`}>
              <div className="max-w-[760px] mx-auto">
                <h2 className="font-display text-2xl sm:text-3xl font-semibold leading-tight text-ink dark:text-dark-ink mb-8 text-center">
                  {journey.label}
                </h2>

                <div className="relative mb-8">
                  <div className="absolute left-5 top-5 bottom-5 w-px bg-line dark:bg-dark-line" aria-hidden="true" />
                  <div className="space-y-6">
                    {journey.steps.map((s, i) => (
                      <div key={s.title} className="relative flex gap-4">
                        <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${c.solid} text-white text-sm font-bold`}>
                          {i + 1}
                        </div>
                        <div className="pt-1.5">
                          <div className="flex items-center gap-2 mb-1">
                            <s.Icon size={15} className={c.text} aria-hidden="true" />
                            <h3 className="text-base font-bold text-ink dark:text-dark-ink">{s.title}</h3>
                          </div>
                          <p className="text-sm text-ink-soft dark:text-dark-ink-soft leading-relaxed">{s.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-center">
                  <a
                    href={journey.cta.url}
                    target="_blank" rel="noopener noreferrer"
                    className={`inline-flex items-center gap-2.5 px-6 py-3 rounded-control font-semibold text-sm text-white transition-opacity hover:opacity-90 ${c.solid}`}
                  >
                    <journey.cta.Icon size={16} />
                    {journey.cta.label}
                    <ArrowRight size={16} strokeWidth={2.5} aria-hidden="true" />
                  </a>
                </div>
              </div>
            </section>
          );
        })}

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
};

export default HowItWorksPage;
